import type {
  MetricValues as CoreMetricValues,
  GroupedMetricOption,
} from '@revelio/core';
import { groupedMetricOptions } from '@revelio/core';
import type {
  CompositionV2EntRespData,
  SentimentMetrics,
} from '@revelio/data-access';

export type CompositionMetricValues = keyof Pick<
  CompositionV2EntRespData,
  | 'headcount'
  | 'growth_rate'
  | 'hiring_rate'
  | 'attrition_rate'
  | 'tenure'
  | 'salary'
>;

const compositionMetrics: Record<CompositionMetricValues, boolean> = {
  headcount: true,
  growth_rate: true,
  hiring_rate: true,
  attrition_rate: true,
  tenure: true,
  salary: true,
};
export const isCompositionMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues =>
  compositionMetrics[metric as CompositionMetricValues];

export type CompositionDerivedMetricValues =
  | 'wage_pressure'
  | 'wage_pressure_percentile'
  | 'salary_growth';

const compositionDerivedMetrics: Record<
  CompositionDerivedMetricValues,
  boolean
> = {
  wage_pressure: true,
  wage_pressure_percentile: true,
  salary_growth: true,
};

export const isCompositionRoutableMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues | CompositionDerivedMetricValues =>
  isCompositionMetric(metric) ||
  compositionDerivedMetrics[metric as CompositionDerivedMetricValues];

export const isNonHeadcountCompositionMetric = (
  metric?: MetricValues
): metric is CompositionMetricValues =>
  compositionMetrics[metric as CompositionMetricValues] &&
  metric !== 'headcount';

export type TransitionMetricValues =
  | 'transitions_inflow'
  | 'transitions_outflow'
  | 'transitions_inflows_and_outflows';
const transitionMetrics: Record<TransitionMetricValues, boolean> = {
  transitions_inflow: true,
  transitions_outflow: true,
  transitions_inflows_and_outflows: true,
};
export const isTransitionMetric = (
  metric?: MetricValues
): metric is TransitionMetricValues =>
  transitionMetrics[metric as TransitionMetricValues];

export type PostingMetricValues =
  | 'posting_salary'
  | 'time_to_fill'
  | 'time_to_fill_percentile'
  | 'postings_active'
  | 'postings_new'
  | 'postings_removed'
  | 'postings_volume';
const postingMetrics: Record<PostingMetricValues, boolean> = {
  posting_salary: true,
  time_to_fill: true,
  time_to_fill_percentile: true,
  postings_active: true,
  postings_new: true,
  postings_removed: true,
  postings_volume: true,
};
export const isPostingMetric = (
  metric?: MetricValues
): metric is PostingMetricValues =>
  postingMetrics[metric as PostingMetricValues];

export type PayMetricValues = Extract<
  MetricValues,
  | 'total_compensation'
  | 'base_salary'
  | 'contractor_pay_rate'
  | 'total_spend_salary'
  | 'total_spend_compensation'
  | 'base_salary_percentiles'
  | 'total_compensation_percentiles'
  | 'contractor_pay_rate_percentiles'
>;
const payMetrics: Record<PayMetricValues, boolean> = {
  total_compensation: true,
  base_salary: true,
  contractor_pay_rate: true,
  total_spend_salary: true,
  total_spend_compensation: true,
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
};
export const isPayMetric = (metric?: MetricValues): metric is PayMetricValues =>
  payMetrics[metric as PayMetricValues];

export type PayPercentileMetricValues = Extract<
  PayMetricValues,
  | 'base_salary_percentiles'
  | 'total_compensation_percentiles'
  | 'contractor_pay_rate_percentiles'
>;
const payPercentileMetrics: Record<PayPercentileMetricValues, boolean> = {
  base_salary_percentiles: true,
  total_compensation_percentiles: true,
  contractor_pay_rate_percentiles: true,
};
export const isPayPercentileMetric = (
  metric?: MetricValues
): metric is PayPercentileMetricValues =>
  payPercentileMetrics[metric as PayPercentileMetricValues];

export type SentimentMetricKeys = keyof Pick<
  SentimentMetrics,
  | 'overall'
  | 'business_outlook'
  | 'career_growth'
  | 'compensation'
  | 'benefits'
  | 'healthcare'
  | 'retirement_pension'
  | 'childcare'
  | 'education_assistance'
  | 'wellness_programs'
  | 'work_life_balance'
  | 'diversity_and_inclusion'
  | 'leadership'
  | 'culture'
  | 'remote_work'
  | 'onboarding'
  | 'mentorship'
  | 'technology'
  | 'promotion_opportunities'
  | 'paid_time_off'
  | 'transparency'
  | 'efficiency_and_bureaucracy'
  | 'product_and_service_quality'
  | 'prestige'
  | 'coworkers'
  | 'mission_and_vision'
  | 'workplace_facilities'
  | 'training'
  | 'clients_and_customers'
  | 'company_growth'
  | 'schedule_flexibility'
  | 'job_security'
  | 'overtime'
  | 'family_support'
  | 'commission'
  | 'independence'
  | 'commute'
  | 'safety'
  | 'internal_communication'
  | 'office_politics'
  | 'harassment'
  | 'honesty'
  | 'ethics'
  | 'commitment'
  | 'compliance'
  | 'integrity'
>;

export type SentimentMetricValues =
  | SentimentMetricKeys
  | 'sentiment_top_bottom';

const sentimentMetrics: Record<SentimentMetricValues, boolean> = {
  sentiment_top_bottom: true,
  overall: true,
  business_outlook: true,
  career_growth: true,
  compensation: true,
  benefits: true,
  healthcare: true,
  retirement_pension: true,
  childcare: true,
  education_assistance: true,
  wellness_programs: true,
  work_life_balance: true,
  diversity_and_inclusion: true,
  leadership: true,
  culture: true,
  remote_work: true,
  onboarding: true,
  mentorship: true,
  technology: true,
  promotion_opportunities: true,
  paid_time_off: true,
  transparency: true,
  efficiency_and_bureaucracy: true,
  product_and_service_quality: true,
  prestige: true,
  coworkers: true,
  mission_and_vision: true,
  workplace_facilities: true,
  training: true,
  clients_and_customers: true,
  company_growth: true,
  schedule_flexibility: true,
  job_security: true,
  overtime: true,
  family_support: true,
  commission: true,
  independence: true,
  commute: true,
  safety: true,
  internal_communication: true,
  office_politics: true,
  harassment: true,
  honesty: true,
  ethics: true,
  commitment: true,
  compliance: true,
  integrity: true,
};
export const isSentimentMetric = (
  metric?: MetricValues
): metric is SentimentMetricValues =>
  sentimentMetrics[metric as SentimentMetricValues];

// Screener metrics
export type ScreenerMetricValues =
  | 'screener_headcount'
  | 'screener_growth'
  | 'screener_growth_yoy'
  | 'screener_hiring'
  | 'screener_attrition'
  | 'screener_salary'
  | 'screener_tenure'
  | 'screener_inflow'
  | 'screener_outflow';

const screenerMetrics: Record<ScreenerMetricValues, boolean> = {
  screener_headcount: true,
  screener_growth: true,
  screener_growth_yoy: true,
  screener_hiring: true,
  screener_attrition: true,
  screener_salary: true,
  screener_tenure: true,
  screener_inflow: true,
  screener_outflow: true,
};

export const isScreenerMetric = (
  metric: MetricValues
): metric is ScreenerMetricValues =>
  screenerMetrics[metric as ScreenerMetricValues];

// Talent Discovery metrics
export type TalentDiscoveryMetricValues = 'td_users';

const talentDiscoveryMetrics: Record<TalentDiscoveryMetricValues, boolean> = {
  td_users: true,
};

export const isTalentDiscoveryMetric = (
  metric?: MetricValues
): metric is TalentDiscoveryMetricValues =>
  talentDiscoveryMetrics[metric as TalentDiscoveryMetricValues];

// Import base type from core and re-export for backward compatibility
export type MetricValues = CoreMetricValues;

export type { GroupedMetricOption, MetricOption } from '@revelio/core';
export { groupedMetricOptions };

export const transitionGroupedMetricOptions: GroupedMetricOption[] = [
  {
    label: 'Flow Type',
    options: [
      { value: 'transitions_inflow', label: 'Inflow' },
      { value: 'transitions_outflow', label: 'Outflow' },
      {
        value: 'transitions_inflows_and_outflows',
        label: 'Inflows & Outflows',
      },
    ],
  },
];

export const sentimentGroupedMetricOptions: GroupedMetricOption[] = [
  {
    label: 'Overview',
    options: [
      { value: 'sentiment_top_bottom', label: 'Sentiment Top/Bottom' },
      { value: 'overall', label: 'Overall Sentiment' },
    ],
  },
  {
    label: 'Compensation & Benefits',
    options: [
      { value: 'compensation', label: 'Compensation Sentiment' },
      { value: 'benefits', label: 'Benefits Sentiment' },
      { value: 'healthcare', label: 'Healthcare Sentiment' },
      { value: 'retirement_pension', label: 'Retirement & Pension Sentiment' },
      { value: 'childcare', label: 'Childcare Sentiment' },
      {
        value: 'education_assistance',
        label: 'Education Assistance Sentiment',
      },
      { value: 'wellness_programs', label: 'Wellness Programs Sentiment' },
      { value: 'commission', label: 'Commission Sentiment' },
    ],
  },
  {
    label: 'Career Growth & Job Security',
    options: [
      { value: 'career_growth', label: 'Career Growth Sentiment' },
      { value: 'onboarding', label: 'Onboarding Sentiment' },
      { value: 'mentorship', label: 'Mentorship Sentiment' },
      {
        value: 'promotion_opportunities',
        label: 'Promotion Opportunities Sentiment',
      },
      { value: 'training', label: 'Training Sentiment' },
      { value: 'job_security', label: 'Job Security Sentiment' },
      { value: 'prestige', label: 'Prestige Sentiment' },
    ],
  },
  {
    label: 'Business & Outlook',
    options: [
      { value: 'business_outlook', label: 'Business Outlook Sentiment' },
      {
        value: 'product_and_service_quality',
        label: 'Product & Service Quality Sentiment',
      },
      { value: 'mission_and_vision', label: 'Mission & Vision Sentiment' },
      {
        value: 'clients_and_customers',
        label: 'Clients & Customers Sentiment',
      },
      { value: 'company_growth', label: 'Company Growth Sentiment' },
    ],
  },
  {
    label: 'Environment & Culture',
    options: [
      { value: 'culture', label: 'Culture Sentiment' },
      { value: 'coworkers', label: 'Coworkers Sentiment' },
      {
        value: 'workplace_facilities',
        label: 'Workplace Facilities Sentiment',
      },
      { value: 'safety', label: 'Safety Sentiment' },
      { value: 'office_politics', label: 'Office Politics Sentiment' },
      { value: 'harassment', label: 'Harassment Sentiment' },
      { value: 'leadership', label: 'Leadership Sentiment' },
      { value: 'independence', label: 'Independence Sentiment' },
    ],
  },
  {
    label: 'Ethics & Values',
    options: [
      {
        value: 'diversity_and_inclusion',
        label: 'Diversity & Inclusion Sentiment',
      },
      { value: 'honesty', label: 'Honesty Sentiment' },
      { value: 'ethics', label: 'Ethics Sentiment' },
      { value: 'commitment', label: 'Commitment Sentiment' },
      { value: 'integrity', label: 'Integrity Sentiment' },
    ],
  },
  {
    label: 'Flexibility & Logistics',
    options: [
      { value: 'work_life_balance', label: 'Work-Life Balance Sentiment' },
      { value: 'remote_work', label: 'Remote Work Sentiment' },
      { value: 'paid_time_off', label: 'Paid Time Off Sentiment' },
      {
        value: 'schedule_flexibility',
        label: 'Schedule Flexibility Sentiment',
      },
      { value: 'commute', label: 'Commute Sentiment' },
      { value: 'overtime', label: 'Overtime Sentiment' },
      { value: 'family_support', label: 'Family Support Sentiment' },
    ],
  },
  {
    label: 'Operations & Efficiency',
    options: [
      { value: 'technology', label: 'Technology Sentiment' },
      { value: 'transparency', label: 'Transparency Sentiment' },
      {
        value: 'efficiency_and_bureaucracy',
        label: 'Efficiency & Bureaucracy Sentiment',
      },
      {
        value: 'internal_communication',
        label: 'Internal Communication Sentiment',
      },
      { value: 'compliance', label: 'Compliance Sentiment' },
    ],
  },
];
export const isFromGroup = (groupName: string, metric: MetricValues) => {
  return groupedMetricOptions
    .find((group) => group.label === groupName)
    ?.options.some((option) => option.value === metric);
};

export const metricOptions = groupedMetricOptions.flatMap(
  (groupedOption) => groupedOption.options
);
